/**
 * Language Redux Helpers
 * ======================
 * Redux-dependent language utilities that need to be separate from languageCore
 * to avoid circular dependencies.
 *
 * Circular dependency chain that was broken:
 * store/index.ts → languageRouterMiddleware → languageCore → store/index.ts (CIRCULAR!)
 *
 * This file contains:
 * - syncLanguageFromUrl (server-side Redux thunk)
 * - syncLanguageFromUrlClient (client-side Redux sync)
 * - languageFromUrlMiddleware (SSR middleware)
 */

import { AppThunk } from "@Store/index";
import { updateUserLanguage } from "@App/appReducer";
import { appShellFromRequest, isNewNativeAppShell } from "constants/appShell";
import {
  extractLanguageFromPath,
  getLanguageFromHostname,
  isSupportedLanguage,
  resolveUserLanguage,
} from "./languageCore";

/**
 * Middleware to sync language from URL path to Redux store
 * Should be called on route changes
 */
export const syncLanguageFromUrl =
  ({ location }: any): AppThunk =>
  async (dispatch, getState) => {
    const currentLanguage = getState().appData.userLanguage;
    const pathLanguage = extractLanguageFromPath(location?.pathname || "");

    // If a valid language is found in the path and it's different from current, update it
    if (pathLanguage && pathLanguage !== currentLanguage) {
      dispatch(updateUserLanguage(pathLanguage));
    }
  };

/**
 * Client-side function to sync language from URL
 */
export const syncLanguageFromUrlClient = (
  pathname: string,
  dispatch: any,
  currentLanguage: string,
) => {
  const pathLanguage = extractLanguageFromPath(pathname);

  // If a valid language is found in the path and it's different from current, update it
  if (pathLanguage && pathLanguage !== currentLanguage) {
    dispatch(updateUserLanguage(pathLanguage));
  }
};

/**
 * Server-side language detection: URL path → app header, then app cookie → domain → `en`.
 *
 * Neither header nor cookie is consulted on the open web (a URL must render the same for
 * everyone, incl. crawlers). The native app shell is the exception — it is never crawled, and
 * cannot use a path prefix because some pages it links to have no language-prefixed route.
 */
export const languageFromUrlMiddleware =
  ({ req }: any): AppThunk =>
  async (dispatch) => {
    const pathname = req.path || req.url || "";
    const hostname = req.host || "";

    // Priority 1: Check URL path for language prefix (e.g., /fr/search)
    const pathLanguage = extractLanguageFromPath(pathname);
    if (pathLanguage && isSupportedLanguage(pathLanguage)) {
      dispatch(updateUserLanguage(pathLanguage));
      return;
    }

    // Priority 1.5: new native app shell only.
    // Gated on `app_shell`, NOT `is_app`: the legacy RN app also sends `is-app` and sets its own
    // `user_language` cookie, and must keep its current behaviour.
    //
    // `resolveUserLanguage` reads the `app-language` header before the cookie. The cookie alone
    // lost a race: the app seeds it into `WKHTTPCookieStore` and navigates immediately, so the
    // next page could still render in the previous language.
    const isNativeAppShell = isNewNativeAppShell(appShellFromRequest(req));
    const appLanguage = isNativeAppShell ? resolveUserLanguage(req) : "";
    if (appLanguage && isSupportedLanguage(appLanguage)) {
      dispatch(updateUserLanguage(appLanguage));
      return;
    }

    // Priority 2: Check domain for language (e.g., amberstudent.cn = zh)
    const domainLanguage = getLanguageFromHostname(hostname);
    if (domainLanguage && isSupportedLanguage(domainLanguage)) {
      dispatch(updateUserLanguage(domainLanguage));
      return;
    }

    // Default: English
    dispatch(updateUserLanguage("en"));
  };
